import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AuthContext from "../Context/AuthProvider";
import { compareDates, convertUTCToTimeZone, convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";
import {
  AppointmentStatus,
  BillingStatus,
  ServiceStatus,
} from "../Utility/Constants";
import { formatAge } from "../Utility/Utility";

import { Modal, Toast } from "bootstrap";
import { getDisplayValue, showLoadingImage } from "../Utility/Utility";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import "../index.css";
import {
  addPrescription,
  getPrescriptionsForAppointment,
  updatePrescription,
} from "../API/PrescriptionApi";
import EditIcon from "@mui/icons-material/Edit";
import PrescriptionsListForAppointment from "../Prescriptions/PrescriptionsListForAppointment";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Fab,
  Grid2,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { getLinkedServices, removeService } from "../API/LinkedServicesApi";
import {
  CloseOutlined,
  DeleteOutlineRounded,
  DisabledByDefault,
  EditOutlined,
  EventNoteOutlined,
  Person,
  PostAdd,
} from "@mui/icons-material";
import AddNewServiceDialog from "../Services/AddNewServiceDialog";
import dayjs from "dayjs";
import { useSnackbar } from "../Utility/UserMessageContext";

function AppointmentDetails({ selectedAppointment }) {
  const { auth } = useContext(AuthContext);
  const [prescriptions, setPrescriptions] = useState();
  const [newStatus, setNewStatus] = useState();
  const [errorMessage, setErrorMessage] = useState();
  const errorToast = new Toast(document.getElementById("errorToast"));
  const [showSpinner, setShowSpinner] = useState(false);
  const [linkedServices, setLinkedServices] = useState();

  const checkInPatientModalId = "checkIn" + selectedAppointment?.id;
  const [currentPrescription, setCurrentPrescription] = useState();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [prescriptionIdToBeEdited, setPrescriptionIdToBeEdited] = useState();
  const [keyForRescheduleRendering, setKeyForRescheduleRendering] = useState(0);
  const [openAddServiceDialog, setOpenAddServiceDialog] = useState();

  const { showSnackbar } = useSnackbar();
  // Check if the screen is small (sm) or extra small (xs)
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setCurrentRow(null);
  };

  const serviceGridColumns = [
    { field: "displayId", headerName: "ID", width: 150 },
    { field: "serviceCode", headerName: "Code", width: 80 },
    { field: "serviceName", headerName: "Name", width: 150 },
    {
      field: "assignedTo",
      headerName: "Assigned To",
      width: 200,
      renderCell: (params) =>
        params?.row?.assignedToUserName
          ? params?.row?.assignedToUserName
          : "Not Assigned",
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => getDisplayValue(params?.row?.status),
    },
    {
      field: "scheduledStartDateTime",
      headerName: "Scheduled Date Time",
      width: 180,
      renderCell: (params) =>
        convertUTCToTimeZoneDateTimeString(params?.row?.scheduledStartDateTime, auth?.selectedFacility?.timeZone),
    },
    {
      field: "actualStartDateTime",
      headerName: "Start Date Time",
      width: 180,
      renderCell: (params) =>
        convertUTCToTimeZoneDateTimeString(params?.row?.actualStartDateTime, auth?.selectedFacility?.timeZone),
    },
    {
      field: "actualCompletionDateTime",
      headerName: "Completion Time",
      width: 180,
      renderCell: (params) =>
        convertUTCToTimeZoneDateTimeString(params?.row?.actualCompletionDateTime, auth?.selectedFacility?.timeZone),
    },
    {
      field: "bill",
      headerName: "Bill",
      width: 180,
      renderCell: (params) =>
        params?.row?.bill ? params?.row?.assignedTo : "Not added to bill",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      headerClassName: "sticky-column", // You can add a custom class to style this column
      cellClassName: "sticky-column", // You can add a custom class to style
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* View Details Icon */}
          {params?.row?.billingStatus === "NotBilled" && (
            <Tooltip title="Add to Bill - yet to be implemented" arrow>
              <IconButton
                variant="outlined"
                //onClick={() => onViewDetails(params.row)}
              >
                <PostAdd fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {params?.row?.billingStatus === "AddedToBill" && (
            <Tooltip title="Remove from Bill - yet to be implemented" arrow>
              <IconButton
                variant="outlined"
                //onClick={() => onViewDetails(params.row)}
              >
                <DisabledByDefault fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {params?.row?.status === ServiceStatus.NotStarted &&
            !params?.row?.isPrimary && (
              <Tooltip title="Remove" arrow>
                <IconButton
                  variant="outlined"
                  onClick={() => onServiceRemove(params.row)}
                >
                  <CloseOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    const loadLinkedServices = async () => {
      const result = await getLinkedServices(auth, selectedAppointment?.id);
      if (result?.isSuccess) {
        setLinkedServices(result?.data);
      } else {
        // error if the services are not loaded.
      }
    };
    loadLinkedServices();
  }, [selectedAppointment, auth]);

  const refreshServiceList = async () => {
    const result = await getLinkedServices(auth, selectedAppointment?.id);
    if (result?.isSuccess) {
      setLinkedServices(result?.data);
    }
  };

  const onServiceRemove = async (item) => {
    if (item.status !== ServiceStatus.NotStarted) {
      showSnackbar(
        "Service cannot be removed as it is already started or completed.",
        "error"
      );
      return;
    } else {
      const result = await removeService(auth, item.id);
      if (result?.isSuccess) {
        refreshServiceList();
      }
    }
  };

  // Render service cards for mobile view
  const renderServiceCards = () => {
    if (!linkedServices || linkedServices.length === 0) {
      return <Typography variant="body2">No services found</Typography>;
    }

    return linkedServices.map((service) => (
      <Card key={service.id} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardHeader
          title={<Box sx={{ mt: 1 }}></Box>}
          subheader={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                {service.displayId}
              </Typography>
              <Chip
                label={getDisplayValue(service.status)}
                size="small"
                color={
                  service.status === ServiceStatus.Completed
                    ? "success"
                    : service.status === ServiceStatus.InProgress
                    ? "primary"
                    : service.status === ServiceStatus.NotStarted
                    ? "default"
                    : "error"
                }
              />
            </Box>
          }
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              {service?.billingStatus === "NotBilled" && (
                <Tooltip title="Add to Bill - yet to be implemented" arrow>
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: "action.hover",
                      "&:hover": { bgcolor: "action.selected" },
                    }}
                  >
                    <PostAdd fontSize="small" color="secondary" />
                  </IconButton>
                </Tooltip>
              )}
              {service?.billingStatus === "AddedToBill" && (
                <Tooltip title="Remove from Bill - yet to be implemented" arrow>
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: "action.hover",
                      "&:hover": { bgcolor: "action.selected" },
                    }}
                  >
                    <DisabledByDefault fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {service?.status === ServiceStatus.NotStarted &&
                !service?.isPrimary && (
                  <Tooltip title="Remove" arrow>
                    <IconButton
                      size="small"
                      onClick={() => onServiceRemove(service)}
                      sx={{
                        bgcolor: "action.hover",
                        "&:hover": { bgcolor: "action.selected" },
                      }}
                    >
                      <CloseOutlined fontSize="small" sx={{ color: "#000" }} />
                    </IconButton>
                  </Tooltip>
                )}
            </Box>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: "0.9rem",
              fontWeight: "500",
              whiteSpace: "normal",
              wordBreak: "break-word",
              mb: 2,
              fontWeight: "bold",
            }}
          >
            {service.serviceName}
          </Typography>
        </CardContent>
        <Divider />
        <CardContent>
          <Stack spacing={1}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Code:</Typography>
              <Typography variant="body2">{service.serviceCode}</Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Assigned To:</Typography>
              <Typography variant="body2">
                {service?.assignedToUserName
                  ? service.assignedToUserName
                  : "Not Assigned"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Scheduled:</Typography>
              <Typography variant="body2">
                {service?.scheduledDateTime
                  ? convertUTCToTimeZone(
                      service?.scheduledDateTime,
                      auth?.selectedFacility?.timeZone
                    ).format("DD-MMM-YYYY hh:mm A")
                  : "Not scheduled"}
              </Typography>
            </Box>

            {service?.startDateTime && (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Started:</Typography>
                <Typography variant="body2">
                  {convertUTCToTimeZone(
                    service?.startDateTime,
                    auth?.selectedFacility?.timeZone
                  ).format("DD-MMM-YYYY hh:mm A")}
                </Typography>
              </Box>
            )}

            {service?.completionTime && (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Completed:</Typography>
                <Typography variant="body2">
                  {convertUTCToTimeZone(
                    service?.completionTime,
                    auth?.selectedFacility?.timeZone
                  ).format("DD-MMM-YYYY hh:mm A")}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Bill:</Typography>
              <Typography variant="body2">
                {service?.bill ? service?.assignedTo : "Not added to bill"}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    ));
  };
  const renderPatientDetailsCard = () => {
    return (
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, width: "100%" }}>
        <CardContent sx={{ padding: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            Patient's Details
          </Typography>

          <Divider sx={{ my: 1 }} />

          {/* Patient details section - now takes full width */}
          <Box sx={{ width: "100%" }}>
            <Box sx={{ "& > div": { mb: 1 } }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Name:</Typography>
                <Typography variant="body2">
                  <Link to="/patient" state={selectedAppointment?.patientId}>
                    {selectedAppointment?.patientFirstName}&nbsp;
                    {selectedAppointment?.patientLastName}
                  </Link>
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">MRN:</Typography>
                <Typography variant="body2">
                  {selectedAppointment.patientMRN || "-"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Age:</Typography>
                <Typography variant="body2">
                  {formatAge(selectedAppointment.patientAgeInYears)}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Gender:</Typography>
                <Typography variant="body2">
                  {selectedAppointment.patientGender || "-"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Contact:</Typography>
                <Typography variant="body2" sx={{ overflowWrap: "break-word" }}>
                  {selectedAppointment.patientMobileNo || "-"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Email:</Typography>
                <Typography variant="body2" sx={{ overflowWrap: "break-word" }}>
                  {selectedAppointment.patientEmailId || "-"}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Photo section - moved below the details */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <img
              src={
                selectedAppointment?.patient?.photo
                  ? selectedAppointment?.patient?.photo
                  : selectedAppointment?.patient?.gender === "Male"
                  ? "./placeholder-image-male-gender.webp"
                  : "./placeholder-image-female-gender.webp"
              }
              className="border rounded p-0"
              alt="PatientPhoto"
              width="80"
              height="80"
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Render visit details card for mobile view
  const renderVisitDetailsCard = () => {
    return (
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, width: "100%" }}>
        <CardContent sx={{ padding: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            Visit Details
          </Typography>

          <Divider sx={{ my: 1 }} />

          <Box sx={{ "& > div": { mb: 1 } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Status:</Typography>
              <Typography variant="body2">
                {selectedAppointment.visitStatus || "-"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Booking Type:</Typography>
              <Typography variant="body2">Walk In</Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Scheduled At:</Typography>
              <Typography variant="body2">
                {dayjs(selectedAppointment.appointmentDateTime).format(
                  "ddd, DD-MMM-YYYY, hh:mm A"
                )}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Booked By:</Typography>
              <Typography variant="body2">
                {selectedAppointment.createdByUserName || "-"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Time of Booking:</Typography>
              <Typography variant="body2">
                {selectedAppointment.createdOn || "-"}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Render primary service card for mobile view
  const renderPrimaryServiceCard = () => {
    return (
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, width: "100%" }}>
        <CardContent sx={{ padding: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            Primary Service Scheduled With
          </Typography>

          <Divider sx={{ my: 1 }} />

          <Box sx={{ "& > div": { mb: 1 } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Name:</Typography>
              <Typography variant="body2">
                {selectedAppointment?.physicianPrefix}&nbsp;
                {selectedAppointment?.physicianFirstName}&nbsp;
                {selectedAppointment?.physicianLastName},&nbsp;
                {selectedAppointment?.physicianSuffix}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">ID:</Typography>
              <Typography variant="body2">
                {selectedAppointment.physicianProfessionalId || "-"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Mobile No.:</Typography>
              <Typography variant="body2">
                {selectedAppointment.physicianMobileNo || "-"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2">Email Id:</Typography>
              <Typography variant="body2">
                {selectedAppointment.physicianEmailId || "-"}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <Grid2
        container
        spacing={2}
        sx={{ paddingX: "5px", paddingTop: "5px", paddingBottom: "50px" , maxHeight: "calc(100vh - 150px)", overflowY: "auto" }}
      >
        <Grid2 container size={12}></Grid2>
        <Grid2 container size={12} sx={{ paddingX: "5px" }}>
          <Grid2 container size={12} sx={{ paddingX: "5px" }}>
            {isSmallScreen ? (
              // Mobile view layout using cards
              <Grid2 container size={12} sx={{ px: 1 }}>
                {renderPatientDetailsCard()}
                {renderVisitDetailsCard()}
                {renderPrimaryServiceCard()}
              </Grid2>
            ) : (
              // Desktop view layout - unchanged
              <Grid2 container size={12} sx={{ paddingX: "5px" }}>
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <Card
                    variant="outlined"
                    sx={{ width: "100%", height: "100%" }}
                  >
                    <CardHeader subheader="Patient's Details"></CardHeader>
                    <CardContent>
                      <Grid2 container size={12}>
                        <Grid2 size={9}>
                          <Grid2 container size={12}>
                            <Grid2 size={3}>
                              <Typography>Name</Typography>
                            </Grid2>
                            <Grid2 size={9}>
                              <Link
                                to="/patient"
                                state={selectedAppointment?.patientId}
                              >
                                {selectedAppointment?.patientFirstName}&nbsp;
                                {selectedAppointment?.patientLastName}
                              </Link>
                            </Grid2>
                          </Grid2>
                          <Grid2 container size={12}>
                            <Grid2 size={3}>
                              <Typography>MRN</Typography>
                            </Grid2>
                            <Grid2 size={9}>
                              <Typography>
                                {selectedAppointment.patientMRN}&nbsp;
                              </Typography>
                            </Grid2>
                          </Grid2>
                          <Grid2 container size={12}>
                            <Grid2 size={3}>
                              <Typography>Age</Typography>
                            </Grid2>
                            <Grid2 size={9}>
                              <Typography>
                                {formatAge(
                                  selectedAppointment.patientAgeInYears
                                )}
                              </Typography>
                            </Grid2>
                          </Grid2>
                          <Grid2 container size={12}>
                            <Grid2 size={3}>
                              <Typography>Gender</Typography>
                            </Grid2>
                            <Grid2 size={9}>
                              <Typography>
                                {selectedAppointment.patientGender}
                              </Typography>
                            </Grid2>
                          </Grid2>
                          <Grid2 container size={12}>
                            <Grid2 size={3}>
                              <Typography>Contact</Typography>
                            </Grid2>
                            <Grid2 size={9}>
                              <Typography sx={{ overflowWrap: "break-word" }}>
                                {selectedAppointment.patientMobileNo}
                              </Typography>
                            </Grid2>
                            <Grid2 size={3}>
                              <Typography>Email</Typography>
                            </Grid2>
                            <Grid2 size={9}>
                              <Typography sx={{ overflowWrap: "break-word" }}>
                                {selectedAppointment.patientEmailId}
                              </Typography>
                            </Grid2>
                          </Grid2>
                        </Grid2>
                        <Grid2
                          container
                          size={3}
                          sx={{
                            overflow: "hidden",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={
                              selectedAppointment?.patient?.photo
                                ? selectedAppointment?.patient?.photo
                                : selectedAppointment?.patient?.gender ===
                                  "Male"
                                ? "./placeholder-image-male-gender.webp"
                                : "./placeholder-image-female-gender.webp"
                            }
                            class=" border rounded p-0"
                            alt="PatientPhoto"
                            width="120"
                            height="120"
                          />
                        </Grid2>
                      </Grid2>
                    </CardContent>
                  </Card>
                </Grid2>
                <Grid2 container size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <Grid2 size={12}>
                    <Card
                      variant="outlined"
                      sx={{ width: "100%", height: "100%" }}
                    >
                      <CardHeader subheader="Visit Details"></CardHeader>
                      <CardContent>
                        <Grid2 container size={12}>
                          <Grid2 size={4}>
                            <Typography>Status</Typography>
                          </Grid2>
                          <Grid2 size={8}>
                            <Typography>
                              {selectedAppointment.visitStatus}
                            </Typography>
                          </Grid2>
                        </Grid2>
                        <Grid2 container size={12}>
                          <Grid2 size={4}>
                            <Typography>Booking Type</Typography>
                          </Grid2>
                          <Grid2 size={8}>
                            <Typography>Walk In</Typography>
                          </Grid2>
                        </Grid2>
                        <Grid2 container size={12}>
                          <Grid2 size={4}>
                            <Typography>Scheduled At</Typography>
                          </Grid2>
                          <Grid2 size={8}>
                            <Typography>
                              {dayjs(
                                selectedAppointment.appointmentDateTime
                              ).format("ddd, DD-MMM-YYYY, hh:mm A")}
                            </Typography>
                          </Grid2>
                        </Grid2>
                        <Grid2 container size={12}>
                          <Grid2 size={4}>
                            <Typography>Booked By</Typography>
                          </Grid2>
                          <Grid2 size={8}>
                            <Typography>
                              {selectedAppointment.createdByUserName}
                            </Typography>
                          </Grid2>
                        </Grid2>
                        <Grid2 container size={12}>
                          <Grid2 size={4}>
                            <Typography>Time of Booking</Typography>
                          </Grid2>
                          <Grid2 size={8}>
                            <Typography>
                              {selectedAppointment.createdOn}
                            </Typography>
                          </Grid2>
                        </Grid2>
                      </CardContent>
                    </Card>
                  </Grid2>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                  <Card
                    variant="outlined"
                    sx={{ width: "100%", height: "100%" }}
                  >
                    <CardHeader subheader="Primary Service Scheduled With"></CardHeader>
                    <CardContent>
                      <Grid2 container size={12}>
                        <Grid2 size={3}>
                          <Typography>Name</Typography>
                        </Grid2>
                        <Grid2 size={9}>
                          <Typography>
                            {selectedAppointment?.physicianPrefix}&nbsp;
                            {selectedAppointment?.physicianFirstName}&nbsp;
                            {selectedAppointment?.physicianLastName},&nbsp;
                            {selectedAppointment?.physicianSuffix}
                          </Typography>
                        </Grid2>
                      </Grid2>
                      <Grid2 container size={12}>
                        <Grid2 size={3}>
                          <Typography>ID</Typography>
                        </Grid2>
                        <Grid2 size={9}>
                          <Typography>
                            {selectedAppointment.physicianProfessionalId}&nbsp;
                          </Typography>
                        </Grid2>
                      </Grid2>
                      <Grid2 container size={12}>
                        <Grid2 size={3}>
                          <Typography>Mobile No.</Typography>
                        </Grid2>
                        <Grid2 size={9}>
                          <Typography>
                            {selectedAppointment.physicianMobileNo}
                          </Typography>
                        </Grid2>
                      </Grid2>
                      <Grid2 container size={12}>
                        <Grid2 size={3}>
                          <Typography>Email Id</Typography>
                        </Grid2>
                        <Grid2 size={9}>
                          <Typography>
                            {selectedAppointment.physicianEmailId}
                          </Typography>
                        </Grid2>
                      </Grid2>
                    </CardContent>
                  </Card>
                </Grid2>
              </Grid2>
            )}
            <Grid2
              container
              size={12}
              sx={{ height: isSmallScreen ? "auto" : "400px", mt: 2 }}
            >
              <Card variant="outlined" sx={{ width: "100%", height: "100%" }}>
                <CardHeader
                  subheader="Availed Services"
                  sx={{ paddingY: "10px" }}
                  action={
                    <Tooltip title="Add new service" arrow>
                      <Fab
                        color="secondary"
                        size="small"
                        onClick={() => setOpenAddServiceDialog(true)}
                        hidden={
                          selectedAppointment.visitStatus ===
                            AppointmentStatus.Completed ||
                          selectedAppointment.visitStatus ===
                            AppointmentStatus.Cancelled ||
                          selectedAppointment.visitStatus ===
                            AppointmentStatus.NoShow
                        }
                      >
                        <AddRoundedIcon sx={{ padding: "0px" }} />
                      </Fab>
                    </Tooltip>
                  }
                ></CardHeader>
                <CardContent
                  sx={{ border: "1px", height: isSmallScreen ? "auto" : "90%" }}
                >
                  {isSmallScreen ? (
                    // Mobile view - Cards
                    <Box sx={{ maxHeight: "60vh", overflow: "auto", px: 1 }}>
                      {renderServiceCards()}
                    </Box>
                  ) : (
                    // Desktop view - DataGrid
                    <DataGrid
                      sx={{
                        height: "100%",
                        "& .sticky-column": {
                          position: "sticky",
                          right: 0,
                          backgroundColor: "white",
                          zIndex: 100,
                        },
                      }}
                      initialState={{ density: "compact" }}
                      columns={serviceGridColumns}
                      rows={linkedServices}
                    ></DataGrid>
                  )}
                </CardContent>
              </Card>
              <AddNewServiceDialog
                openState={openAddServiceDialog}
                setOpenState={setOpenAddServiceDialog}
                visitId={selectedAppointment?.id}
                refreshServices={refreshServiceList}
                visitType={selectedAppointment?.visitType}
                visitLocation={selectedAppointment?.location}
                visitDate={convertUTCToTimeZoneDateTimeString(
                  selectedAppointment?.visitDateTime,
                  auth?.selectedFacility?.timeZone
                )}

               
                
              ></AddNewServiceDialog>
            </Grid2>
          </Grid2>
        </Grid2>
      </Grid2>
    </>
  );
}

export default AppointmentDetails;
